import { t } from "@bn-games/i18n-core";
import { gameLabel, type Invite } from "@bn-games/social-client";
import { clear, el } from "../components/dom";

const ENVELOPE_GLYPH = "✉";
const CLOSE_GLYPH = "×";

let popoverSeq = 0;

export interface InvitePillHandle {
  /** Wrapper holding the pill and its popover; hidden while there's no invite. */
  readonly root: HTMLElement;
  /** Shows `invite` (with `card` as the popover body), or hides on `null`. */
  update(invite: Invite | null, total: number, card: HTMLElement | null): void;
  close(restoreFocus: boolean): void;
  /** The pill's own box — not the popover's — or `null` while hidden. */
  footprint(): DOMRectReadOnly | null;
  destroy(): void;
}

/**
 * The compact, in-game face of the invite dock: a small top-left button that
 * toggles a non-modal popover holding the same invite card the menus show.
 * It never opens itself and never takes focus on arrival — the dock announces
 * arrivals through the shared live region instead.
 */
export function createInvitePill(): InvitePillHandle {
  popoverSeq += 1;
  const popoverId = `invite-popover-${popoverSeq}`;

  const name = el("span", { class: "invite-pill__name", attrs: { "aria-hidden": "true" } });
  const count = el("span", { class: "invite-pill__count", attrs: { "aria-hidden": "true" } });
  const pill = el(
    "button",
    {
      class: "invite-pill",
      attrs: { type: "button", "aria-expanded": "false", "aria-controls": popoverId },
      on: { click: () => toggle() },
    },
    [el("span", { class: "invite-pill__glyph", text: ENVELOPE_GLYPH, attrs: { "aria-hidden": "true" } }), name, count],
  );

  const closeButton = el("button", {
    class: "invite-popover__close",
    text: CLOSE_GLYPH,
    attrs: { type: "button" },
    on: { click: () => close(true) },
  });
  const body = el("div", { class: "invite-popover__body" });
  const popover = el("div", { class: "invite-popover", attrs: { id: popoverId, role: "region" } }, [
    closeButton,
    body,
  ]);
  popover.hidden = true;

  const root = el("div", { class: "invite-pill-wrap" }, [pill, popover]);
  root.hidden = true;

  let open = false;

  // Registered only while the popover is open, in the window's capture phase so
  // it runs before the games' own Escape-for-pause listeners — and swallowing
  // the key only then, so a closed popover never steals Escape from a game.
  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    event.stopPropagation();
    close(true);
  }

  function setOpen(next: boolean): void {
    open = next;
    pill.setAttribute("aria-expanded", String(next));
    popover.hidden = !next;
    if (next) window.addEventListener("keydown", onKeydown, true);
    else window.removeEventListener("keydown", onKeydown, true);
  }

  function close(restoreFocus: boolean): void {
    if (!open) return;
    setOpen(false);
    if (restoreFocus) pill.focus();
  }

  function toggle(): void {
    if (open) close(false);
    else setOpen(true);
  }

  return {
    root,
    update(invite, total, card): void {
      if (invite === null || card === null) {
        close(false);
        root.hidden = true;
        clear(body);
        return;
      }
      const sender = invite.from.displayName;
      const game = gameLabel(invite.game);
      const more = total - 1;
      const aria = t("social.dock.pillAria", { sender, game });
      pill.setAttribute(
        "aria-label",
        more > 0 ? `${aria}. ${t("social.invite.moreCount", { count: more })}` : aria,
      );
      name.textContent = t("social.dock.pillLabel", { sender });
      count.textContent = String(total);
      closeButton.setAttribute("aria-label", t("social.dock.close"));
      popover.setAttribute("aria-label", t("social.dock.popoverLabel"));

      // Re-rendering the card (busy/error changes) would otherwise drop focus
      // to <body> from a button that just disappeared.
      const active = document.activeElement;
      const hadFocus = active !== null && body.contains(active);
      clear(body);
      body.appendChild(card);
      root.hidden = false;
      if (hadFocus) closeButton.focus();
    },
    close,
    footprint(): DOMRectReadOnly | null {
      return root.hidden ? null : pill.getBoundingClientRect();
    },
    destroy(): void {
      close(false);
      root.remove();
    },
  };
}
